import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {Button, Tooltip} from 'antd';
import {useNavigate} from 'react-router-dom';
import {
  StyledInvoiceNavigationButtons,
  StyledInvoiceNavigationButtonsSpace,
} from './StyledInvoiceNavigationButtons';
import DeleteModal from '../../../DeleteModal';
import {ChevronLeftIcon, ChevronRightIcon, TrashIcon, WireIcon} from '../../../../icons';
import {alertActions} from '../../../../state/actions/alert.actions';
import {invoicesActions} from '../../../../state/actions/invoice.actions';
import {firebaseEvents} from '../../../../snippets/firebase';
import {systemHelpers} from '../../../../utils/helpers';
import routes from '../../../../routes/routes.json';

const extractButtonProps = (props = {}) => {
  const { enabled, ...filtered } = props;
  return { enabled, filtered };
};

const InvoiceNavigationButtons = ({
  batchDeleteInvoices,
  dispatch,
  invoice,
  backButtonProps,
  neighborIds = { next: null, prev: null },
  nextButtonProps,
  payButtonProps,
  previousButtonProps,
  ...rest
}) => {
  const [deleteModalProps, setDeleteModalProps] = useState({loading: false, open: false});
  const [t] = useTranslation(['main', 'invoices']);
  const navigate = useNavigate();

  const { enabled: showBack, filtered: filteredBackProps } = extractButtonProps(backButtonProps);
  const { enabled: showNext, filtered: filteredNextProps } = extractButtonProps(nextButtonProps);
  const { enabled: showPay, filtered: filteredPayProps } = extractButtonProps(payButtonProps);
  const { enabled: showPrevious, filtered: filteredPreviousProps } = extractButtonProps(previousButtonProps);

  const handleDelete = () => setDeleteModalProps({...deleteModalProps, open: true});

  const handleCancelDeleteInvoiceModal = () => {
    setDeleteModalProps({...deleteModalProps, loading: false, open: false});
  }

  const handleOkDeleteInvoiceModal = () => {
    const id = invoice?.id;
    if (!id) return;

    setDeleteModalProps({...deleteModalProps, loading: true});
    batchDeleteInvoices(
      [id],
      () => {
        const nextId = neighborIds?.next;
        const redirectPageUrl = nextId
          ? `${routes.invoicesList}/${nextId}`
          : routes.invoicesList;

        alertActions.success(t('invoices:successInvoiceDeleteMessage'));
        handleCancelDeleteInvoiceModal();
        systemHelpers.logEvent(firebaseEvents.INVOICES_DELETE_INVOICE);
        navigate(redirectPageUrl);
      },
      handleCancelDeleteInvoiceModal
    );
  }

  return (
    <StyledInvoiceNavigationButtons
      {...rest}
    >
      {showBack && (
        <Button
          icon={<ChevronLeftIcon />}
          size='large'
          {...filteredBackProps}
        >
          {t('back')}
        </Button>
      )}

      <StyledInvoiceNavigationButtonsSpace size='small' wrap='true'>

        {showPay && (
          <Button
            icon={<WireIcon />}
            size='large'
            type='primary'
            {...filteredPayProps}
          >
            {t('pay')} {t('now')}
          </Button>
        )}

        <Tooltip title={t('delete')}>
          <Button
            danger
            icon={<TrashIcon />}
            onClick={handleDelete}
            size='large'
          />
        </Tooltip>

        {showPrevious && (
          <Tooltip title={t('previous')}>
            <Button
              disabled
              icon={<ChevronLeftIcon />}
              size='large'
              {...filteredPreviousProps}
            />
          </Tooltip>
        )}

        {showNext && (
          <Tooltip title={t('next')}>
            <Button
              icon={<ChevronRightIcon />}
              size='large'
              {...filteredNextProps}
            />
          </Tooltip>
        )}
      </StyledInvoiceNavigationButtonsSpace>

      <DeleteModal
        description={t(`invoices:modal.delete.description`)}
        onCancel={handleCancelDeleteInvoiceModal}
        onOk={handleOkDeleteInvoiceModal}
        title={t(`invoices:modal.delete.title`)}
        {...deleteModalProps}
      />
    </StyledInvoiceNavigationButtons>
  );
}

InvoiceNavigationButtons.propTypes = {
  invoice: PropTypes.object,
  backButtonProps: PropTypes.object,
  neighborIds: PropTypes.shape({
    next: PropTypes.string,
    prev: PropTypes.string
  }),
  nextButtonProps: PropTypes.object,
  payButtonProps: PropTypes.object,
  previousButtonProps: PropTypes.object,
}

const mapDispatchToProps = {
  batchDeleteInvoices: invoicesActions.batchDeleteInvoices,
}

export default connect(null, mapDispatchToProps)(InvoiceNavigationButtons);
